import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';
import {
  KIND_OPTIONS,
  STATUS_OPTIONS,
  contact_of,
  fmt_dt,
  status_color,
  where_or_company,
} from './helpers';
import type { Booking, BookingsListResponse } from './types';
import BookingDrawer from './BookingDrawer';
import Badge from './Badge';

interface TenantListItem {
  tenant_id: string;
  name: string;
}

interface Filters {
  status: string;
  kind: string;
  from: string; // datetime-local value
  to: string;
  tenant_id: string;
}

const EMPTY_FILTERS: Filters = { status: '', kind: '', from: '', to: '', tenant_id: '' };

/**
 * Cross-tenant booking list with filters + cursor pagination. Reused in two places:
 * the top-level Bookings view (no pin) and the per-tenant card (pinned_tenant_id), where
 * the tenant filter + business column are hidden. Row click opens the detail drawer.
 */
export default function BookingsList({ pinned_tenant_id }: { pinned_tenant_id?: string }) {
  const [filters, set_filters] = useState<Filters>(EMPTY_FILTERS);
  // Cursor pagination: pages[i] is the cursor that loads page i (page 0 = undefined).
  const [pages, set_pages] = useState<(number | undefined)[]>([undefined]);
  const [page_index, set_page_index] = useState(0);
  const [open_id, set_open_id] = useState<string | null>(null);

  // Reset pagination whenever filters change.
  useEffect(() => {
    set_pages([undefined]);
    set_page_index(0);
  }, [filters.status, filters.kind, filters.from, filters.to, filters.tenant_id]);

  // Tenant options for the tenant filter (top-level view only).
  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<{ tenants: TenantListItem[] }>('/api/tenants'),
    enabled: !pinned_tenant_id,
  });

  const effective_tenant = pinned_tenant_id ?? (filters.tenant_id || undefined);
  const cursor = pages[page_index];

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.status) p.set('status', filters.status);
    if (filters.kind) p.set('kind', filters.kind);
    if (filters.from) p.set('from', new Date(filters.from).toISOString());
    if (filters.to) p.set('to', new Date(filters.to).toISOString());
    if (effective_tenant) p.set('tenant_id', effective_tenant);
    if (cursor !== undefined) p.set('cursor', String(cursor));
    return p.toString();
  }, [filters, effective_tenant, cursor]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['bookings', effective_tenant ?? '', qs],
    queryFn: () =>
      api.get<BookingsListResponse>(`/api/captomi-office/bookings${qs ? `?${qs}` : ''}`),
  });

  function go_next() {
    if (data?.next_cursor === undefined) return;
    const next_pages = pages.slice(0, page_index + 1);
    next_pages.push(data.next_cursor);
    set_pages(next_pages);
    set_page_index(page_index + 1);
  }
  function go_prev() {
    if (page_index === 0) return;
    set_page_index(page_index - 1);
  }

  const columns = [
    ...(pinned_tenant_id
      ? []
      : [
          {
            header: 'Business',
            cell: (b: Booking) => (
              <strong>{b.business_name ?? b.tenant_id}</strong>
            ),
          },
        ]),
    { header: 'When', cell: (b: Booking) => fmt_dt(b.timestamp), width: '170px' },
    {
      header: 'Kind',
      cell: (b: Booking) =>
        b.booking_kind ? <code style={{ fontSize: 12 }}>{b.booking_kind}</code> : '—',
    },
    {
      header: 'Status',
      cell: (b: Booking) => <Badge color={status_color(b.status)}>{b.status}</Badge>,
    },
    { header: 'Client', cell: (b: Booking) => b.client_name ?? '—' },
    {
      header: 'Contact',
      cell: (b: Booking) => <span style={{ fontSize: 13 }}>{contact_of(b)}</span>,
    },
    {
      header: 'Where / company',
      cell: (b: Booking) => (
        <span style={{ fontSize: 13 }}>{where_or_company(b)}</span>
      ),
    },
  ];

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Status</span>
            <select
              value={filters.status}
              onChange={(e) => set_filters((f) => ({ ...f, status: e.target.value }))}
            >
              <option value="">(all)</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Kind</span>
            <select
              value={filters.kind}
              onChange={(e) => set_filters((f) => ({ ...f, kind: e.target.value }))}
            >
              <option value="">(all)</option>
              {KIND_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>From</span>
            <input
              type="datetime-local"
              value={filters.from}
              onChange={(e) => set_filters((f) => ({ ...f, from: e.target.value }))}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>To</span>
            <input
              type="datetime-local"
              value={filters.to}
              onChange={(e) => set_filters((f) => ({ ...f, to: e.target.value }))}
            />
          </label>
          {!pinned_tenant_id && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Tenant</span>
              <select
                value={filters.tenant_id}
                onChange={(e) => set_filters((f) => ({ ...f, tenant_id: e.target.value }))}
              >
                <option value="">(all tenants)</option>
                {(tenantsQuery.data?.tenants ?? [])
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((t) => (
                    <option key={t.tenant_id} value={t.tenant_id}>
                      {t.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {(filters.status || filters.kind || filters.from || filters.to || filters.tenant_id) && (
            <button className="secondary" onClick={() => set_filters(EMPTY_FILTERS)}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading && <p style={{ padding: 24, margin: 0 }}>Loading bookings…</p>}
        {error && (
          <p className="error" style={{ margin: 24 }}>
            Failed to load: {error instanceof ApiError ? error.reason : String(error)}
          </p>
        )}
        {data && (
          <>
            <DataTable
              rows={data.bookings}
              columns={columns}
              on_row_click={(b) => set_open_id(b.booking_id)}
              empty_message="No bookings match these filters."
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                borderTop: '1px solid var(--line)',
              }}
            >
              <button
                className="secondary"
                onClick={go_prev}
                disabled={page_index === 0 || isFetching}
              >
                ← Previous
              </button>
              <span className="muted" style={{ fontSize: 13 }}>
                Page {page_index + 1}
                {isFetching ? ' · loading…' : ''}
              </span>
              <button
                className="secondary"
                onClick={go_next}
                disabled={data.next_cursor === undefined || isFetching}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>

      {open_id && (
        <BookingDrawer booking_id={open_id} on_close={() => set_open_id(null)} />
      )}
    </div>
  );
}
